import React, { useState, useRef, useEffect } from 'react';

// Standardized AGS Astra assistant button + chat widget.
// Frontend-only: answers are computed from the inverter data already loaded
// in the app — no backend call, no API/functionality change.
const IconSparkles = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>);
const IconX = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>);
const IconSend = (p) => (<svg {...p} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>);

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

function answerFrom(question, data) {
  const q = (question || '').toLowerCase();
  const sites = data?.sites || [];
  if (!sites.length) {
    return "I don't see any loaded data yet. Upload an Excel file from the **Upload** page (or open **Dashboard**) and I can summarize site health, inverter counts, and alerts for you.";
  }
  const totalInverters = sites.reduce((s, x) => s + (x.inverter_details?.length || 0), 0);
  const avgHealth = data?.overall_stats?.avg_health_ratio;
  const alerts = sites.filter(s => (s.health_ratio || 0) < 85);
  const healthy = sites.filter(s => (s.health_ratio || 0) >= 90);
  const sorted = [...sites].sort((a, b) => (a.health_ratio || 0) - (b.health_ratio || 0));

  if (q.includes('alert') || q.includes('attention') || q.includes('worst') || q.includes('low')) {
    if (!alerts.length) return 'Good news — **no sites are below 85% health** right now. Everything is performing well.';
    const list = sorted.slice(0, 5).filter(s => (s.health_ratio || 0) < 85)
      .map(s => `• **${s.site_name}** — ${(s.health_ratio || 0).toFixed(1)}%`).join('\n');
    return `**${alerts.length}** site${alerts.length > 1 ? 's' : ''} need attention (below 85% health):\n${list}`;
  }
  if (q.includes('best') || q.includes('top') || q.includes('healthiest')) {
    const list = [...sites].sort((a, b) => (b.health_ratio || 0) - (a.health_ratio || 0)).slice(0, 5)
      .map(s => `• **${s.site_name}** — ${(s.health_ratio || 0).toFixed(1)}%`).join('\n');
    return `Top performing sites:\n${list}`;
  }
  if (q.includes('inverter')) {
    return `There are **${fmt(totalInverters)} inverters** across **${fmt(sites.length)} sites** in the current dataset.`;
  }
  if (q.includes('average') || q.includes('avg') || q.includes('overall') || q.includes('health')) {
    return `Average health ratio across **${fmt(sites.length)} sites** is **${avgHealth != null ? avgHealth.toFixed(1) + '%' : 'N/A'}**. ${healthy.length} site${healthy.length !== 1 ? 's are' : ' is'} at or above 90%.`;
  }
  if (q.includes('site') || q.includes('summary') || q.includes('overview')) {
    return `**Summary:** ${fmt(sites.length)} sites · ${fmt(totalInverters)} inverters · avg health ${avgHealth != null ? avgHealth.toFixed(1) + '%' : 'N/A'} · ${alerts.length} need attention.`;
  }
  return "I can summarize the loaded inverter data. Try: *site summary*, *which sites need attention*, *average health*, *total inverters*, or *top sites*.";
}

const AssistantAgent = ({ data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm your **VoltIQ Assistant**.\n\nAsk me about the data you've loaded:\n- Site health summary\n- Which sites need attention\n- Average health & inverter counts" }
  ]);
  const [input, setInput] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  const send = () => {
    const question = input.trim();
    if (!question) return;
    setInput('');
    const reply = answerFrom(question, data);
    setMessages(prev => [...prev, { role: 'user', text: question }, { role: 'assistant', text: reply }]);
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  const md = (t) => t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>').replace(/\n/g, '<br/>');

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-50 transition-transform hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #86bb46 0%, #679632 100%)' }}
        title="VoltIQ Assistant"
      >
        <IconSparkles className="w-7 h-7 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 lg:bottom-6 lg:right-6 z-50 flex flex-col w-[calc(100vw-2rem)] sm:w-[380px] lg:w-[400px]" style={{ maxHeight: '80vh', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', borderRadius: 12 }}>
      <div className="flex items-center gap-3 px-4 py-3 rounded-t-xl text-white" style={{ background: 'linear-gradient(135deg, #86bb46 0%, #679632 100%)' }}>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"><IconSparkles className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">VoltIQ Assistant</p>
          <p className="text-xs text-white/80 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" />Grounded in your loaded data</p>
        </div>
        <button type="button" onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"><IconX className="w-[18px] h-[18px]" /></button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white px-4 py-3 space-y-3 border-x border-gray-200" style={{ maxHeight: '380px', minHeight: '200px' }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5" style={{ backgroundColor: 'rgba(103,150,50,0.1)' }}>
                <IconSparkles className="w-3.5 h-3.5" style={{ color: '#679632' }} />
              </div>
            )}
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${msg.role === 'user' ? 'text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm border border-gray-200'}`}
              style={msg.role === 'user' ? { backgroundColor: '#679632' } : undefined}
              dangerouslySetInnerHTML={{ __html: md(msg.text) }}
            />
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="bg-white px-3 py-2 border-x border-gray-200 flex flex-wrap gap-1.5">
        {['Site summary', 'Which sites need attention', 'Average health'].map(q => (
          <button key={q} type="button" onClick={() => setInput(q)} className="text-xs px-2.5 py-1 rounded-full transition-colors" style={{ border: '1px solid rgba(103,150,50,0.3)', color: '#679632' }}>
            {q}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 bg-white px-3 py-3 border border-gray-200 rounded-b-xl">
        <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} placeholder="Ask about your inverter data..." className="flex-1 text-sm outline-none placeholder-gray-400 bg-transparent" />
        <button type="button" onClick={send} disabled={!input.trim()} className="p-2 rounded-lg text-gray-400 hover:text-[#679632] disabled:opacity-30 transition-colors"><IconSend className="w-[18px] h-[18px]" /></button>
      </div>
    </div>
  );
};

export default AssistantAgent;
